import { ChevronDown } from "lucide-react";
import { FC, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import {
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "~/components/ui/command";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";

import { FilteredCommand } from "~/components/FilteredCommand";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Separator } from "~/components/ui/separator";

import { DoclistTreeItem, DoclistTreeItemSectionsItem } from "~/api/model";
import { trackEvent } from "~/utils/analytics";

interface MergeSectionDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  section: DoclistTreeItemSectionsItem;
  items: DoclistTreeItem[];
  onMerge: (
    mergedSection: DoclistTreeItemSectionsItem,
    removedSectionId: string
  ) => void;
  defaultTargetSection?: DoclistTreeItemSectionsItem | null;
}

export const MergeSectionDialog: FC<MergeSectionDialogProps> = ({
  open,
  setOpen,
  section,
  items,
  onMerge,
  defaultTargetSection,
}) => {
  const intl = useIntl();
  const [mergedSectionName, setMergedSectionName] = useState(section.name);
  const [mergedSectionError, setMergedSectionError] = useState(false);
  const [value, setValue] = useState(section.id);
  const [secondValue, setSecondValue] = useState<string | null>(
    defaultTargetSection?.id ?? null
  );
  const [openFirstSectionDropdown, setOpenFirstSectionDropdown] =
    useState(false);
  const [openSecondSectionDropdown, setOpenSecondSectionDropdown] =
    useState(false);

  useEffect(() => {
    if (openFirstSectionDropdown || openSecondSectionDropdown) {
      trackEvent({
        eventName: "app/doc_list/merge_document_modal/document.select/click",
      });
    }
  }, [openFirstSectionDropdown, openSecondSectionDropdown]);

  useEffect(() => {
    if (open) {
      setMergedSectionName(section.name);
      setMergedSectionError(false);
      if (defaultTargetSection) {
        setSecondValue(defaultTargetSection.id);
      }
    } else {
      setValue(section.id);
      setSecondValue(null);
      setMergedSectionName(section.name);
      setMergedSectionError(false);
    }
  }, [open, section, items, defaultTargetSection]);

  useEffect(() => {
    if (value === secondValue) {
      setSecondValue(null);
    }
  }, [value, secondValue]);

  useEffect(() => {
    if (secondValue === value) {
      setValue(section.id);
    }
  }, [secondValue, value, section]);

  const getMergeableSections = (selectedSectionId: string | null) => {
    if (!selectedSectionId) return items;

    return items
      .map((item) => ({
        ...item,
        sections: item.sections.filter((s) => s.id !== selectedSectionId),
      }))
      .filter((item) => item.sections.length > 0);
  };

  const handleBlurMergedSection = () => {
    if (!mergedSectionName.trim()) {
      setMergedSectionError(true);
    } else {
      setMergedSectionError(false);
    }
  };

  const handleMerge = () => {
    if (!secondValue) return;
    const secondSection = items
      .flatMap((item) => item.sections)
      .find((s) => s.id === secondValue);
    if (!secondSection) return;

    const firstSection = items
      .flatMap((item) => item.sections)
      .find((s) => s.id === value);
    if (!firstSection) return;

    const mergedSection = {
      ...firstSection,
      name: mergedSectionName,
      pageIds: [...firstSection.pageIds, ...secondSection.pageIds],
      pages: [...(firstSection.pages ?? []), ...(secondSection.pages ?? [])],
      pageCount: secondSection.pageCount + firstSection.pageCount,
    };
    onMerge(mergedSection, secondValue);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="w-full max-w-[720px]">
        <DialogHeader>
          <DialogTitle>
            <FormattedMessage
              id="docList.mergeSectionDialog.title"
              defaultMessage="Merge Document"
            />
          </DialogTitle>
          <DialogDescription>
            <FormattedMessage
              id="docList.mergeSectionDialog.description"
              defaultMessage="Select the documents you want to merge."
            />
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2 max-w-[672px]">
          <Label>
            <FormattedMessage
              id="docList.mergeSectionDialog.firstSection"
              defaultMessage="First Document"
            />
          </Label>
          <Popover
            open={openFirstSectionDropdown}
            onOpenChange={setOpenFirstSectionDropdown}
            modal={true}
          >
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={openFirstSectionDropdown}
                className="justify-between font-normal"
              >
                <span className="text-ellipsis overflow-hidden truncate">
                  {value
                    ? items
                        .flatMap((item) => item.sections)
                        .find((c) => c.id === value)?.name
                    : section.name}
                </span>
                <ChevronDown className="opacity-50 w-4 h-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[calc(100vw-48px)] sm:w-[672px] max-w-[672px] p-0 cursor-pointer">
              <FilteredCommand>
                <CommandInput
                  placeholder={intl.formatMessage({
                    id: "docList.moveToDialog.placeholder",
                    defaultMessage: "Start typing",
                  })}
                />
                <CommandList className="max-h-[200px]">
                  <CommandEmpty>
                    <FormattedMessage
                      id="docList.mergeSectionDialog.noFound"
                      defaultMessage="No section found"
                    />
                  </CommandEmpty>
                  {items.map((item) => (
                    <CommandGroup
                      heading={item.category.name}
                      key={item.category.id}
                    >
                      {item.sections.map((section) => (
                        <CommandItem
                          key={section.id}
                          value={section.id}
                          keywords={[section.name]}
                          onSelect={(currentValue) => {
                            const selectedSection = item.sections.find(
                              (s) => s.id === currentValue
                            );
                            if (selectedSection) {
                              setValue(selectedSection.id);
                              setOpenFirstSectionDropdown(false);
                            }
                          }}
                        >
                          {section.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  ))}
                </CommandList>
              </FilteredCommand>
            </PopoverContent>
          </Popover>
        </div>
        <div className="flex flex-col gap-2 max-w-[672px]">
          <Label>
            <FormattedMessage
              id="docList.mergeSectionDialog.secondSection"
              defaultMessage="Second Document"
            />
          </Label>
          <Popover
            open={openSecondSectionDropdown}
            onOpenChange={setOpenSecondSectionDropdown}
            modal={true}
          >
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={openSecondSectionDropdown}
                className="justify-between font-normal"
              >
                <span className="text-ellipsis overflow-hidden truncate">
                  {secondValue
                    ? items
                        .flatMap((item) => item.sections)
                        .find((c) => c.id === secondValue)?.name
                    : intl.formatMessage({
                        id: "docList.mergeSectionDialog.selectSecond",
                        defaultMessage: "Select second document",
                      })}
                </span>
                <ChevronDown className="opacity-50 w-4 h-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[calc(100vw-48px)] sm:w-[672px] max-w-[672px] p-0 cursor-pointer">
              <FilteredCommand>
                <CommandInput
                  placeholder={intl.formatMessage({
                    id: "docList.moveToDialog.placeholder",
                    defaultMessage: "Start typing",
                  })}
                />
                <CommandList className="max-h-[200px]">
                  <CommandEmpty className="p-2 text-sm">
                    <FormattedMessage
                      id="docList.mergeSectionDialog.noFound"
                      defaultMessage="No section found"
                    />
                  </CommandEmpty>
                  {getMergeableSections(value).map((item) => (
                    <CommandGroup
                      heading={item.category.name}
                      key={item.category.id}
                    >
                      {item.sections.map((section) => (
                        <CommandItem
                          key={section.id}
                          value={section.id}
                          keywords={[section.name]}
                          onSelect={(currentValue) => {
                            setSecondValue(currentValue);
                            setOpenSecondSectionDropdown(false);
                          }}
                        >
                          {section.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  ))}
                </CommandList>
              </FilteredCommand>
            </PopoverContent>
          </Popover>
        </div>
        <Separator />
        <div className="flex flex-col gap-2">
          <Label htmlFor="mergedSectionName">
            <FormattedMessage
              id="docList.mergeSectionDialog.mergedSectionName"
              defaultMessage="First Document Name"
            />
          </Label>
          <Input
            id="mergedSectionName"
            type="text"
            value={mergedSectionName}
            onChange={(e) => {
              setMergedSectionName(e.target.value);
            }}
            required
            onBlur={handleBlurMergedSection}
            placeholder={intl.formatMessage({
              id: "docList.splitSectionDialog.placeholder",
              defaultMessage: "Enter document name",
            })}
            className={`${mergedSectionError && "border-destructive"}`}
          />
          {mergedSectionError && (
            <span className="text-sm text-destructive">
              <FormattedMessage
                id="docList.splitSectionDialog.placeholder"
                defaultMessage="Enter document name"
              />
            </span>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <DialogClose asChild>
            <Button type="button" variant="outline">
              <FormattedMessage id="cancel" defaultMessage="Cancel" />
            </Button>
          </DialogClose>
          <Button
            onClick={handleMerge}
            disabled={!mergedSectionName || !secondValue}
          >
            <FormattedMessage
              id="docList.mergeSectionDialog.merge"
              defaultMessage="Merge"
            />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
